/**
 * GlobalSearchCommand Component
 *
 * グローバル検索機能 (Cmd+K / Ctrl+K)
 * shadcn/ui CommandDialogを使用した統合検索UI
 *
 * Features:
 * - Cmd+K / Ctrl+K でトグル
 * - ナビゲーションショートカット (⌘D, ⌘R, ⌘C等)
 * - リアルタイム検索 (顧客、予約)
 * - AI相談統合
 *
 * Design System v3.0準拠
 */

import * as React from "react"
import {
  Calendar,
  Settings,
  Users,
  UserCog,
  LayoutDashboard,
  TrendingUp,
  Sparkles,
  Building2,
  HelpCircle,
} from "lucide-react"
import { useNavigate } from "react-router-dom"

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command"
import { useStore } from "@/store/useStore"

export function GlobalSearchCommand() {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const navigate = useNavigate()
  const { customers, reservations } = useStore()

  // Cmd+K / Ctrl+K でトグル
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  // 検索結果フィルタリング
  const filteredCustomers = React.useMemo(() => {
    if (!query) return []
    const searchLower = query.toLowerCase()
    return customers
      .filter(c =>
        c.name.toLowerCase().includes(searchLower) ||
        c.phone.includes(query) ||
        c.email.toLowerCase().includes(searchLower)
      )
      .slice(0, 5)
  }, [query, customers])

  const filteredReservations = React.useMemo(() => {
    if (!query) return []
    const searchLower = query.toLowerCase()
    return reservations
      .filter(r =>
        r.customerName?.toLowerCase().includes(searchLower) ||
        r.reservationNumber?.includes(query)
      )
      .slice(0, 5)
  }, [query, reservations])

  const handleSelect = (callback: () => void) => {
    setOpen(false)
    setQuery("")
    callback()
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="顧客、予約、商品を検索... (Ctrl+K)"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>結果が見つかりません。</CommandEmpty>

        {/* ナビゲーションショートカット */}
        {!query && (
          <>
            <CommandGroup heading="ナビゲーション">
              <CommandItem onSelect={() => handleSelect(() => navigate("/dashboard"))}>
                <LayoutDashboard className="mr-2 h-4 w-4" />
                <span>ダッシュボード</span>
                <CommandShortcut>⌘D</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/reservations"))}>
                <Calendar className="mr-2 h-4 w-4" />
                <span>予約管理</span>
                <CommandShortcut>⌘R</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/customers"))}>
                <Users className="mr-2 h-4 w-4" />
                <span>顧客管理</span>
                <CommandShortcut>⌘C</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/staff"))}>
                <UserCog className="mr-2 h-4 w-4" />
                <span>スタッフ管理</span>
                <CommandShortcut>⌘T</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/sales"))}>
                <TrendingUp className="mr-2 h-4 w-4" />
                <span>売上分析</span>
                <CommandShortcut>⌘S</CommandShortcut>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="設定・ヘルプ">
              <CommandItem onSelect={() => handleSelect(() => navigate("/organization/settings"))}>
                <Building2 className="mr-2 h-4 w-4" />
                <span>組織設定</span>
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/settings"))}>
                <Settings className="mr-2 h-4 w-4" />
                <span>設定</span>
              </CommandItem>
              <CommandItem onSelect={() => handleSelect(() => navigate("/help"))}>
                <HelpCircle className="mr-2 h-4 w-4" />
                <span>ヘルプ</span>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="AIアシスタント">
              <CommandItem onSelect={() => handleSelect(() => navigate("/ai-chat"))}>
                <Sparkles className="mr-2 h-4 w-4 text-indigo-600" />
                <span>AI相談を開始</span>
                <CommandShortcut>⌘A</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          </>
        )}

        {/* 顧客検索結果 */}
        {query && filteredCustomers.length > 0 && (
          <>
            <CommandGroup heading="顧客">
              {filteredCustomers.map((customer) => (
                <CommandItem
                  key={customer.id}
                  onSelect={() => handleSelect(() => navigate(`/customers/${customer.id}`))}
                >
                  <Users className="mr-2 h-4 w-4" />
                  <span>{customer.name}</span>
                  <CommandShortcut>{customer.phone}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* 予約検索結果 */}
        {query && filteredReservations.length > 0 && (
          <>
            <CommandGroup heading="予約">
              {filteredReservations.map((reservation) => (
                <CommandItem
                  key={reservation.id}
                  onSelect={() => handleSelect(() => navigate(`/reservations/${reservation.id}`))}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>{reservation.customerName} - {new Date(reservation.date).toLocaleDateString('ja-JP')}</span>
                  <CommandShortcut>{reservation.reservationNumber}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* AI検索提案 */}
        {query && (filteredCustomers.length > 0 || filteredReservations.length > 0) && (
          <>
            <CommandSeparator />
            <CommandGroup heading="AI分析">
              <CommandItem onSelect={() => handleSelect(() => navigate(`/ai-chat?query=${encodeURIComponent(query)}`))}>
                <Sparkles className="mr-2 h-4 w-4 text-indigo-600" />
                <span>「{query}」についてAIに相談</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  )
}
